import { selectedBackend } from './api';
import { nextBackoff, type ConnStatus, OFFLINE_AFTER } from './ws-client';

// AgentFrame is one JSON TEXT frame from /api/agents/{id}/ws. On connect the
// server replays history (as user/assistant_text/tool_use/state frames) then
// streams live turn events. `input` is the tool's raw argument object.
export type AgentFrame =
  | { type: 'user'; text: string }
  | { type: 'assistant_text'; text: string }
  | { type: 'tool_use'; name: string; input?: unknown }
  | { type: 'turn_done' }
  | { type: 'state'; busy: boolean }
  | { type: 'error'; message: string };

interface Handlers {
  onEvent: (frame: AgentFrame) => void;
  onStatus?: (s: ConnStatus) => void;
}

// AgentSocket is a reconnecting WebSocket for an agent's event stream. It mirrors
// TerminalSocket's backoff/offline logic but parses JSON TEXT frames (the agent
// channel is never binary). Each reconnect replays history, so the consumer must
// reset its transcript on (re)connect — agent-view does this on 'connecting'.
export class AgentSocket {
  private ws?: WebSocket;
  private attempt = 0;
  private closed = false;

  constructor(
    private agentId: string,
    private h: Handlers
  ) {
    this.h.onStatus?.('connecting');
    this.connect();
  }

  private url(): string {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    // Browsers cannot set custom headers on WebSocket connections, so the
    // chosen backend rides along as a ?backend= query param (same as terminals).
    const id = selectedBackend();
    const q = id ? `?backend=${encodeURIComponent(id)}` : '';
    return `${proto}://${location.host}/api/agents/${this.agentId}/ws${q}`;
  }

  private connect() {
    const ws = new WebSocket(this.url());
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.h.onStatus?.('connected');
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      let frame: AgentFrame;
      try {
        frame = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.h.onEvent(frame);
    };
    ws.onclose = () => {
      if (this.closed) return;
      const a = this.attempt++;
      this.h.onStatus?.(a >= OFFLINE_AFTER ? 'offline' : 'reconnecting');
      setTimeout(() => this.connect(), nextBackoff(a));
    };
  }

  close() {
    this.closed = true;
    this.ws?.close();
  }
}
